import type { ScriptLine } from "../../coldcompute";

// Caption lines are SCENE-LOCAL (fromSec resets to 0 inside each scene's <Sequence>).
// VO == on-screen caption. Every claim is grounded in EAKC-MVP:
//   model registry / tiers  → src/lib/agents/models.ts, src/lib/model-settings.ts,
//                             docs/visual-companion/2026-05-10-three-tier-model-strategy.html
//   complexity router       → src/lib/rag/query-router.ts (STRATEGY_CONFIGS)
//   semantic cache          → src/lib/rag/semantic-cache.ts (0.97 / 30min / LRU 100)
//   embeddings              → src/lib/rag/embeddings.ts (Qwen3, $0.01/M, 1536-dim)
//   ledger + breaker + 402  → src/lib/llm/openrouter-call.ts, ISSUE-RATE-LIMIT-402.md,
//                             docs/visual-companion/2026-05-17-cost-incident-defense.html
// No fabricated aggregate cost-reduction % — every number on screen is a code constant
// or a measured figure (the $55/$1.80 incident, the 0.97 threshold, the per-tier knobs).

export const HOOK: ScriptLine[] = [
  { scene: "hook", fromSec: 0.3, holdSec: 4.4, caption: "My RAG worked perfectly — and quietly burned money. Same answer billed twice; 'what is X?' routed to a frontier model." },
];

export const PROBLEM: ScriptLine[] = [
  { scene: "problem", fromSec: 0.4, holdSec: 5.2, caption: "RAG was built to fix retrieval quality — never cost. In production the two collide, silently." },
  { scene: "problem", fromSec: 5.8, holdSec: 5.4, caption: "Three classic leaks: over-fetching context, no caching, and one expensive model for every query." },
  { scene: "problem", fromSec: 11.6, holdSec: 4.0, caption: "No error, no alert. The system keeps answering. The bill just grows." },
];

export const TIERS: ScriptLine[] = [
  { scene: "tiers", fromSec: 0.4, holdSec: 5.4, caption: "Layer one: every call site declares who reads the output. Cheap by default — escalate only when it's earned." },
  { scene: "tiers", fromSec: 6.0, holdSec: 5.4, caption: "Five task-purpose tiers. Routing and Explore stay on a Flash-class model; only human-facing prose goes to the strong tier." },
  { scene: "tiers", fromSec: 11.6, holdSec: 4.0, caption: "The spread is ~40× on input tokens. Sending simple traffic to the top tier is pure, invisible waste." },
];

export const ROUTER: ScriptLine[] = [
  { scene: "router", fromSec: 0.4, holdSec: 5.4, caption: "Layer two kills over-fetching: a 7-strategy router makes retrieval depth a function of the question." },
  { scene: "router", fromSec: 6.0, holdSec: 5.6, caption: "Simple: one pass, top-5, 4K budget, no reasoning. Deep-research: three passes, top-20, a 16K budget." },
  { scene: "router", fromSec: 11.8, holdSec: 4.2, caption: "And a hard query can upgrade its own tier. The cheap path stays cheap; the hard path gets what it needs." },
];

export const CACHE: ScriptLine[] = [
  { scene: "cache", fromSec: 0.4, holdSec: 5.4, caption: "Layer three: a semantic cache. Match query embeddings at cosine 0.97 and serve the stored answer — no LLM call." },
  { scene: "cache", fromSec: 6.0, holdSec: 5.4, caption: "Upstash Redis on the fast path, Postgres as the durable source of truth. 30-minute TTL, true LRU." },
  { scene: "cache", fromSec: 11.6, holdSec: 4.2, caption: "Invalidation fails CLOSED — a re-synced source wipes its rows. A redundant call beats a stale answer." },
];

export const CODE: ScriptLine[] = [
  { scene: "code", fromSec: 0.5, holdSec: 6.5, caption: "None of this is abstract. The cache threshold and TTL are constants in semantic-cache.ts." },
  { scene: "code", fromSec: 7.5, holdSec: 7.0, caption: "Each router strategy is a recipe — passes, top-K, token budget, temperature, whether to reason at all." },
  { scene: "code", fromSec: 15.0, holdSec: 7.0, caption: "And the embedder is Qwen3 at a penny per million tokens — half the old cost, higher recall. Cheaper AND better." },
];

export const GUARD: ScriptLine[] = [
  { scene: "guard", fromSec: 0.4, holdSec: 5.6, caption: "Layer four, learned the hard way: in five days OpenRouter billed $55 while my dashboard read $1.80." },
  { scene: "guard", fromSec: 6.2, holdSec: 5.6, caption: "Cost was only recorded on success — failed retry loops spent invisibly. Now every call writes a ledger row." },
  { scene: "guard", fromSec: 12.0, holdSec: 5.0, caption: "Three guards stop runaway missions; out-of-quota returns 402 Payment Required, not a misleading 429." },
];

export const OUTRO: ScriptLine[] = [
  { scene: "outro", fromSec: 0.4, holdSec: 5.2, caption: "No magic 85%. Just real constants: make every model call justify itself, and record what you spend." },
  { scene: "outro", fromSec: 5.9, holdSec: 4.2, caption: "RAG gets you the right answers. This gets you the right bill — and a number you can trust." },
];

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
